import { useState } from 'react';

export default function CapitalGain() {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleUpload = async () => {
    if (!file) { setError('Please select a PDF file'); return; }
    setLoading(true);
    setError('');
    setSuccess(false);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch('/api/upload/pdf-to-excel', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Upload failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name.replace('.pdf', '_capital_gain.xlsx');
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-12">
      <div className="text-center mb-10">
        <span className="text-5xl">📈</span>
        <h1 className="text-3xl font-bold text-gray-800 mt-4">
          Capital Gain Calculator
        </h1>
        <p className="text-gray-500 mt-3 max-w-xl mx-auto">
          Upload your capital gain report PDF from any broker —
          Zerodha, Groww, AngelOne, HDFC Securities, or mutual fund platforms.
          Get a clean Excel report with tax calculation instantly.
        </p>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border p-8">
        <div
          className="border-2 border-dashed border-blue-200 rounded-xl p-10 text-center cursor-pointer hover:border-blue-400 transition"
          onClick={() => document.getElementById('fileInput')?.click()}>
          <input
            id="fileInput"
            type="file"
            accept=".pdf"
            className="hidden"
            onChange={e => setFile(e.target.files?.[0] || null)}
          />
          {file ? (
            <div>
              <div className="text-4xl mb-3">📄</div>
              <p className="font-semibold text-gray-700">{file.name}</p>
              <p className="text-sm text-gray-400 mt-1">
                {(file.size / 1024).toFixed(1)} KB
              </p>
              <button
                className="mt-3 text-sm text-blue-600 hover:underline"
                onClick={e => { e.stopPropagation(); setFile(null); }}>
                Change file
              </button>
            </div>
          ) : (
            <div>
              <div className="text-4xl mb-3">☁️</div>
              <p className="text-gray-600 font-medium">
                Click to upload your Capital Gain PDF
              </p>
              <p className="text-sm text-gray-400 mt-2">
                Supports: Zerodha, Groww, AngelOne, HDFC, Mutual Fund platforms
              </p>
            </div>
          )}
        </div>

        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {success && (
          <div className="mt-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
            Report downloaded successfully!
          </div>
        )}

        <button
          onClick={handleUpload}
          disabled={!file || loading}
          className="w-full mt-6 bg-blue-700 text-white py-3 rounded-xl font-semibold hover:bg-blue-800 transition disabled:opacity-50 disabled:cursor-not-allowed text-lg">
          {loading ? 'Processing... This may take 30-60 seconds' : 'Generate Capital Gain Report'}
        </button>

        <div className="mt-6 bg-blue-50 rounded-xl p-4">
          <p className="text-sm font-semibold text-blue-800 mb-2">
            What you will get:
          </p>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>Capital Gain Summary (scheme-wise)</li>
            <li>All transactions in one clean sheet</li>
            <li>Tax calculation (STCG u/s 111A and LTCG u/s 112A)</li>
            <li>Ready to use for ITR filing</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
